/**
 * Sub-Accounts Service Module
 *
 * Manages sub-account operations and management.
 */

import { api } from './client';
import type { SubAccount, MasterAccount, ListResponse, PaginationParams } from './types';

/**
 * Sub-Accounts API
 *
 * Endpoints for sub-account management.
 */
export const subAccountsAPI = {
  /**
   * Get sub-accounts
   *
   * Retrieves list of sub-accounts under current account.
   *
   * **Endpoint:** GET /api/sub-accounts/
   * **Protected:** Yes
   */
  getSubAccounts: async (params?: PaginationParams): Promise<ListResponse<SubAccount>> =>
    api.get<ListResponse<SubAccount>>('/api/sub-accounts', params),

  /**
   * Get master account info
   *
   * Retrieves master account information.
   *
   * **Endpoint:** GET /api/sub-accounts/master
   * **Protected:** Yes
   */
  getMasterAccount: async (): Promise<MasterAccount> =>
    api.get<MasterAccount>('/api/sub-accounts/master'),

  getWalletSummary: async () =>
    api.get<{
      master: { id: number; username: string; level_code: string; balances: Record<string, string>; ap_queue: string };
      uo_total: string;
      sub_count: number;
      sub_accounts: Array<{
        id: number;
        username: string;
        level_code: string;
        status: string;
        balances: Record<string, string>;
      }>;
    }>('/api/sub-accounts/summary'),

  openPreview: (params?: {
    placement_user_id?: number;
    star_package?: 'E1' | 'E2' | 'E3';
    reinvest_coin_type?: 'sp1' | 'sp3';
  }) => api.get('/api/sub-accounts/open/preview', params),

  open: (body: {
    reinvest_coin_type: 'sp1' | 'sp3';
    transaction_password: string;
    security_answer: string;
    position: 'L' | 'R';
    placement_user_id?: number;
    star_package?: 'E1' | 'E2' | 'E3';
  }) => api.post('/api/sub-accounts/open', body),
};
